// ===========================================
// Enrollment Report Filters Component
// ===========================================
// Filters for enrollment analytics
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import { EnrollmentReportFilters } from '../../api/reports.api';
import { useTerms, useLessonTypes } from '../../hooks/useAdmin';
import { useTeachers } from '../../hooks/useUsers';

interface EnrollmentFiltersProps {
  filters: EnrollmentReportFilters;
  onChange: (filters: EnrollmentReportFilters) => void;
}

const EnrollmentFilters: React.FC<EnrollmentFiltersProps> = ({ filters, onChange }) => {
  const { data: termsData, isLoading: termsLoading } = useTerms();
  const { data: lessonTypesData, isLoading: lessonTypesLoading } = useLessonTypes();
  const { data: teachersData, isLoading: teachersLoading } = useTeachers();

  const terms = termsData || [];
  const lessonTypes = lessonTypesData || [];
  const teachers = teachersData || [];

  const handleTermChange = (event: SelectChangeEvent<string>) => {
    onChange({
      ...filters,
      termId: event.target.value || undefined,
    });
  };

  const handleLessonTypeChange = (event: SelectChangeEvent<string>) => {
    onChange({
      ...filters,
      lessonTypeId: event.target.value || undefined,
    });
  };

  const handleTeacherChange = (event: SelectChangeEvent<string>) => {
    onChange({
      ...filters,
      teacherId: event.target.value || undefined,
    });
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Filters
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="term-filter-label">Term</InputLabel>
          <Select
            labelId="term-filter-label"
            id="term-filter"
            value={filters.termId || ''}
            onChange={handleTermChange}
            label="Term"
            disabled={termsLoading}
          >
            <MenuItem value="">
              <em>All Terms</em>
            </MenuItem>
            {terms.map((term) => (
              <MenuItem key={term.id} value={term.id}>
                {term.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="lesson-type-filter-label">Lesson Type</InputLabel>
          <Select
            labelId="lesson-type-filter-label"
            id="lesson-type-filter"
            value={filters.lessonTypeId || ''}
            onChange={handleLessonTypeChange}
            label="Lesson Type"
            disabled={lessonTypesLoading}
          >
            <MenuItem value="">
              <em>All Types</em>
            </MenuItem>
            {lessonTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="teacher-filter-label">Teacher</InputLabel>
          <Select
            labelId="teacher-filter-label"
            id="teacher-filter"
            value={filters.teacherId || ''}
            onChange={handleTeacherChange}
            label="Teacher"
            disabled={teachersLoading}
          >
            <MenuItem value="">
              <em>All Teachers</em>
            </MenuItem>
            {teachers.map((teacher) => (
              <MenuItem key={teacher.id} value={teacher.userId}>
                {teacher.User.firstName} {teacher.User.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Paper>
  );
};

export default EnrollmentFilters;
